import { useState, useEffect } from 'react';
import api from '../lib/api';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, ScatterChart, Scatter, CartesianGrid, Legend, Cell, PieChart, Pie, LineChart, Line, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from 'recharts';
import { Wifi, Signal, Radio, AlertTriangle, TrendingUp, MapPin, Building2, Activity, Zap } from 'lucide-react';

const DISTRICT_COLORS = { TS17: '#FE4A2A', TS18: '#2D6E4E', TS19: '#8A1A1A', TS20: '#1A4D8A', TS21: '#8A5A14', TS22: '#5C2D8A', TS23: '#090909' };
const QUADRANT_COLORS = { 'High Infra + High Adoption': '#2D6E4E', 'High Infra + Low Adoption (INTERVENTION GAP)': '#FE4A2A', 'Low Infra + High Adoption': '#1A4D8A', 'Low Infra + Low Adoption (CRITICAL)': '#8A1A1A' };

export default function ConnectivityDashboard() {
  const [data, setData] = useState(null);
  const [imdDist, setImdDist] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/connectivity/dashboard'),
      api.get('/connectivity/imd-distribution'),
    ]).then(([dash, imd]) => {
      setData(dash.data);
      setImdDist(imd.data);
    }).catch(console.error);
  }, []);

  if (!data) return <div className="flex items-center justify-center min-h-[400px] font-body text-sm text-ink/40">Loading connectivity data...</div>;

  const { ofcom_fixed: fb, ofcom_mobile: mob, ofcom_fibre_takeup: ft, imd_la_summary: imd, imd_vs_digital: ivd, connectivity_matrix: matrix } = data;
  const scopeName = data.scope === 'tvca' || !data.scope ? 'Tees Valley' : (data.scope_name || 'Tees Valley');
  const is2025Imd = imd && imd.imd_rank_of_average_rank_2025 != null;

  // Prepare IMD vs Digital chart
  const imdChart = (ivd || []).map(d => ({ ...d, decile: `D${d.imd_decile}` }));

  // Prepare IMD distribution by district (aggregated)
  const imdByDistrict = {};
  imdDist.forEach(r => {
    if (!imdByDistrict[r.district]) imdByDistrict[r.district] = { district: r.district, total: 0, most_deprived: 0, avg_score: 0, count: 0 };
    imdByDistrict[r.district].total += r.count;
    imdByDistrict[r.district].avg_score += r.avg_score * r.count;
    imdByDistrict[r.district].count += r.count;
    if (r.decile <= 2) imdByDistrict[r.district].most_deprived += r.count;
  });
  const districtImd = Object.values(imdByDistrict).map(d => ({
    ...d,
    avg_score: d.count ? +(d.avg_score / d.count).toFixed(2) : 0,
    deprivation_pct: d.total ? +(d.most_deprived / d.total * 100).toFixed(1) : 0,
  })).sort((a, b) => b.deprivation_pct - a.deprivation_pct);

  return (
    <div className="max-w-[1400px] mx-auto px-4 lg:px-6 py-6 space-y-6" data-testid="connectivity-dashboard">
      <div>
        <h1 className="font-display text-2xl sm:text-3xl font-extrabold text-ink">Connectivity &amp; Deprivation</h1>
        <p className="font-body text-base text-ink/70 font-medium mt-1">Infrastructure vs. Adoption Reality</p>
        <p className="font-body text-sm text-ink/50 max-w-3xl leading-relaxed mt-2">
          {scopeName} has world-class broadband infrastructure — {fb?.gigabit_pct || 0}% Gigabit, {mob?.['4g_outdoor_pct'] || mob?.['4g_outdoor_all_operators_pct'] || 0}% 4G outdoor. Yet deprivation persists across its most deprived neighbourhoods. This dashboard reveals the gap between what infrastructure exists and what is actually being used — essential for understanding where adoption support is needed, not where infrastructure investment is.
        </p>
      </div>

      {/* ── KPI Row: Ofcom Fixed + Mobile ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        {[
          { label: 'Premises', value: (fb?.premises ?? fb?.all_premises)?.toLocaleString() || '—', icon: Building2, color: 'text-ink' },
          { label: 'Gigabit', value: `${fb?.gigabit_pct || 0}%`, icon: Zap, color: 'text-status-green' },
          { label: 'Full-Fibre', value: `${fb?.full_fibre_pct || 0}%`, icon: Wifi, color: 'text-info-blue' },
          { label: 'Ultrafast', value: `${fb?.ufbb_pct ?? ft?.takeup_pct_all_premises ?? 0}%`, icon: TrendingUp, color: 'text-brand-orange' },
          { label: '4G Indoor', value: `${mob?.['4g_indoor_pct'] ?? mob?.['4g_indoor_all_operators_pct'] ?? 0}%`, icon: Signal, color: 'text-ai-purple' },
          { label: '4G Outdoor', value: `${mob?.['4g_outdoor_pct'] ?? mob?.['4g_outdoor_all_operators_pct'] ?? 0}%`, icon: Radio, color: 'text-status-amber' },
          { label: 'Below USO', value: `${fb?.below_uso_pct || 0}%`, icon: AlertTriangle, color: 'text-status-red' },
          { label: 'Superfast', value: `${fb?.sfbb_pct ?? fb?.fwa_decent_pct ?? 0}%`, icon: Radio, color: 'text-status-green' },
          { label: 'IMD Rank', value: is2025Imd ? `#${imd.imd_rank_of_average_rank_2025}` : `${((imd?.imd_extent || 0) * 100).toFixed(0)}%`, icon: MapPin, color: 'text-status-red' },
        ].map((kpi, i) => (
          <div key={i} className="solid-card p-3 text-center animate-fade-in" style={{ animationDelay: `${i * 0.03}s` }} data-testid={`conn-kpi-${i}`}>
            <kpi.icon className={`w-3.5 h-3.5 mx-auto mb-1 ${kpi.color}`} strokeWidth={1.5} />
            <span className="font-body text-[9px] uppercase tracking-[0.1em] text-ink/40 block">{kpi.label}</span>
            <span className={`font-display text-lg font-extrabold ${kpi.color}`}>{kpi.value}</span>
          </div>
        ))}
      </div>

      {/* ── Connectivity vs Adoption Matrix ── */}
      <div className="solid-card p-5">
        <h3 className="font-display text-lg font-extrabold text-ink mb-1 flex items-center gap-2">
          <Activity className="w-4 h-4 text-brand-orange" strokeWidth={1.5} /> Connectivity vs Adoption Matrix
        </h3>
        <p className="font-body text-xs text-ink/40 mb-4">Infrastructure quality (Ofcom) plotted against digital adoption (register data) per district</p>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ left: 10, bottom: 30, right: 10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="infra_score" name="Infrastructure" type="number" domain={[0, 100]}
                tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }}
                label={{ value: 'Infrastructure Score', position: 'bottom', fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <YAxis dataKey="adoption_score" name="Adoption" type="number" domain={[0, 50]}
                tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }}
                label={{ value: 'Adoption Score', angle: -90, position: 'insideLeft', fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <Tooltip content={({ payload }) => {
                if (!payload?.[0]) return null;
                const d = payload[0].payload;
                return (
                  <div className="solid-card p-2 font-body text-xs">
                    <p className="font-display font-extrabold">{d.district} — {d.name}</p>
                    <p>Infra: {d.infra_score} | Adoption: {d.adoption_score}</p>
                    <p>Gigabit: {d.gigabit_pct}% | IMD: {d.imd_avg_decile}</p>
                    <p className="font-medium mt-1" style={{ color: QUADRANT_COLORS[d.quadrant] }}>{d.quadrant}</p>
                  </div>
                );
              }} />
              <Scatter data={matrix}>
                {matrix.map((d, i) => (
                  <Cell key={i} fill={QUADRANT_COLORS[d.quadrant] || '#999'} r={Math.max(8, Math.min(20, d.registered_in_db / 80))} />
                ))}
              </Scatter>
            </ScatterChart>
          </ResponsiveContainer>
          <div className="space-y-2">
            {matrix.sort((a, b) => a.adoption_score - b.adoption_score).map(d => (
              <div key={d.district} className="flex items-center gap-3 p-2 rounded-[4px] bg-ink/3">
                <div className="w-3 h-3 rounded-full shrink-0" style={{ background: QUADRANT_COLORS[d.quadrant] }} />
                <div className="flex-1 min-w-0">
                  <span className="font-display text-sm font-extrabold text-ink">{d.district}</span>
                  <span className="font-body text-[10px] text-ink/40 ml-2">{d.name}</span>
                </div>
                <div className="text-right shrink-0">
                  <span className="font-body text-[10px] text-ink/50 block">Infra {d.infra_score} | Adopt {d.adoption_score}</span>
                  <span className="font-body text-[9px] uppercase tracking-wider" style={{ color: QUADRANT_COLORS[d.quadrant] }}>{d.quadrant.split('(')[0].trim()}</span>
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-ink/10 font-body text-[10px] text-ink/30 leading-relaxed">
              <span className="font-medium text-brand-orange">INTERVENTION GAP</span> = broadband exists but businesses aren't using it. This is a people problem, not a pipes problem.
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ── IMD vs Digital Score ── */}
        <div className="solid-card p-5">
          <h3 className="font-display text-lg font-extrabold text-ink mb-1">Deprivation vs Digital Adoption</h3>
          <p className="font-body text-xs text-ink/40 mb-3">IMD Decile 1 = most deprived, 10 = least deprived</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={imdChart} barGap={1}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis dataKey="decile" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <YAxis tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 4 }} />
              <Legend wrapperStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <Bar dataKey="avg_digital_score" name="Avg Digital Score" fill="#FE4A2A" radius={[2, 2, 0, 0]} />
              <Bar dataKey="pct_digitally_active" name="% Digitally Active" fill="#2D6E4E" radius={[2, 2, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ── District Deprivation Profile ── */}
        <div className="solid-card p-5">
          <h3 className="font-display text-lg font-extrabold text-ink mb-1">District Deprivation Profile</h3>
          <p className="font-body text-xs text-ink/40 mb-3">% of businesses in most deprived postcodes (IMD Decile 1-2)</p>
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={districtImd} layout="vertical" barSize={16}>
              <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
              <XAxis type="number" tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <YAxis dataKey="district" type="category" width={45} tick={{ fontFamily: "'IBM Plex Mono'", fontSize: 10 }} />
              <Tooltip contentStyle={{ fontFamily: "'IBM Plex Mono'", fontSize: 11, borderRadius: 4 }} />
              <Bar dataKey="deprivation_pct" name="% Most Deprived" radius={[0, 2, 2, 0]}>
                {districtImd.map((d, i) => (
                  <Cell key={i} fill={d.deprivation_pct > 40 ? '#8A1A1A' : d.deprivation_pct > 20 ? '#8A5A14' : '#2D6E4E'} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        {/* ── Broadband Coverage Breakdown ── */}
        <div className="solid-card p-5">
          <h3 className="font-display text-lg font-extrabold text-ink mb-1 flex items-center gap-2">
            <Wifi className="w-4 h-4 text-info-blue" strokeWidth={1.5} /> Fixed Broadband Coverage
          </h3>
          <div className="space-y-3 mt-3">
            {[
              { label: 'Superfast (SFBB)', value: fb?.sfbb_pct, color: '#2D6E4E' },
              { label: 'Ultrafast (UFBB)', value: fb?.ufbb_pct, color: '#1A4D8A' },
              { label: 'Full-Fibre (FTTP)', value: fb?.full_fibre_pct, color: '#5C2D8A' },
              { label: 'Gigabit', value: fb?.gigabit_pct, color: '#FE4A2A' },
              { label: 'NGA', value: fb?.nga_pct, color: '#090909' },
              { label: 'FWA Decent BB', value: fb?.fwa_decent_pct, color: '#2D6E4E' },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex justify-between font-body text-xs mb-0.5">
                  <span className="text-ink/60">{item.label}</span>
                  <span className="text-ink font-medium">{item.value || 0}%</span>
                </div>
                <div className="h-2 bg-ink/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${item.value || 0}%`, background: item.color }} />
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-ink/10 space-y-1">
              <div className="flex justify-between font-body text-xs">
                <span className="text-ink/40">Fibre take-up (of coverage)</span>
                <span className="text-brand-orange font-medium">{ft?.takeup_pct_coverage || 0}%</span>
              </div>
              <div className="flex justify-between font-body text-xs">
                <span className="text-ink/40">Fibre take-up (all premises)</span>
                <span className="text-brand-orange font-medium">{ft?.takeup_pct_all_premises || 0}%</span>
              </div>
              <div className="flex justify-between font-body text-xs">
                <span className="text-ink/40">Below USO</span>
                <span className="text-status-red font-medium">{fb?.below_uso_pct || 0}% ({fb?.premises_below_uso || 0} premises)</span>
              </div>
            </div>
          </div>
        </div>

        {/* ── Mobile Coverage ── */}
        <div className="solid-card p-5">
          <h3 className="font-display text-lg font-extrabold text-ink mb-1 flex items-center gap-2">
            <Signal className="w-4 h-4 text-ai-purple" strokeWidth={1.5} /> Mobile Coverage
          </h3>
          <div className="space-y-3 mt-3">
            {[
              { label: '4G Outdoor (all ops)', value: mob?.['4g_outdoor_all_operators_pct'], color: '#2D6E4E' },
              { label: '4G Indoor (all ops)', value: mob?.['4g_indoor_all_operators_pct'], color: '#1A4D8A' },
              { label: '5G Outdoor (high conf)', value: mob?.['5g_outdoor_all_operators_pct'], color: '#5C2D8A' },
              { label: '5G VHC Outdoor', value: mob?.['5g_vhc_outdoor_all_pct'], color: '#FE4A2A' },
              { label: '2G Outdoor', value: mob?.['2g_outdoor_all_pct'], color: '#8A5A14' },
            ].map((item, i) => (
              <div key={i}>
                <div className="flex justify-between font-body text-xs mb-0.5">
                  <span className="text-ink/60">{item.label}</span>
                  <span className="text-ink font-medium">{item.value || 0}%</span>
                </div>
                <div className="h-2 bg-ink/5 rounded-full overflow-hidden">
                  <div className="h-full rounded-full transition-all" style={{ width: `${item.value || 0}%`, background: item.color }} />
                </div>
              </div>
            ))}
            <div className="pt-2 border-t border-ink/10">
              <div className="flex justify-between font-body text-xs">
                <span className="text-ink/40">4G no operator (indoor)</span>
                <span className="text-status-red font-medium">{mob?.['4g_indoor_no_operator_pct'] || 0}%</span>
              </div>
              <div className="flex justify-between font-body text-xs">
                <span className="text-ink/40">5G no operator (outdoor)</span>
                <span className="text-status-amber font-medium">{mob?.['5g_outdoor_no_operator_pct'] || 0}%</span>
              </div>
              <div className="flex justify-between font-body text-xs">
                <span className="text-ink/40">Total premises</span>
                <span className="text-ink font-medium">{mob?.premises?.toLocaleString() || 0}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── IMD LA Summary ── */}
      {imd && (
        <div className="solid-card p-5 border-l-4 border-l-status-red bg-status-red/[0.02]">
          <h3 className="font-display text-lg font-extrabold text-ink mb-3">{scopeName} — IMD {is2025Imd ? '2025' : '2019'} Summary</h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {(is2025Imd ? [
              { label: 'Overall IMD Rank', value: `${imd.imd_rank_of_average_rank_2025}/296`, sub: 'Most deprived LAs' },
              { label: 'Avg IMD Score', value: `${(imd.imd_average_score_2025 || 0).toFixed(1)}`, sub: 'MHCLG IoD 2025' },
              { label: 'Avg LSOA Rank', value: `${Math.round(imd.imd_average_rank_2025 || 0).toLocaleString()}`, sub: 'of 33,755 LSOAs' },
            ] : [
              { label: 'Overall IMD Rank', value: `${imd.imd_rank_of_average_rank}/317`, sub: 'Most deprived LAs' },
              { label: 'Most Deprived 10%', value: `${(imd.imd_proportion_most_deprived_10pct * 100).toFixed(1)}%`, sub: 'LSOAs nationally' },
              { label: 'Employment Rank', value: `${imd.employment_rank}/317`, sub: 'Worst in England' },
              { label: 'Health Rank', value: `${imd.health_rank}/317`, sub: 'Deprivation & disability' },
              { label: 'Living Env. Rank', value: `${imd.living_environment_rank}/317`, sub: 'Worst in England' },
            ]).map((item, i) => (
              <div key={i} className="text-center p-2 rounded-[4px] bg-white/50">
                <span className="font-body text-[9px] uppercase tracking-[0.1em] text-ink/40 block">{item.label}</span>
                <span className="font-display text-xl font-extrabold text-status-red">{item.value}</span>
                <span className="font-body text-[9px] text-ink/30 block">{item.sub}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <p className="font-body text-xs text-ink/25 text-center leading-relaxed">
        Data sources: Ofcom Connected Nations (Jan 2026) · MHCLG IoD 2025 · Companies House · Nexintell Digital Register.
        Infrastructure scores are weighted: Gigabit 50%, UFBB 30%, SFBB 20%. Adoption scores: DB adoption rate 60%, avg digital score 40%.
        <a href="/privacy" className="underline">Privacy Policy</a>
      </p>
    </div>
  );
}
